import { type ApiSuccess } from './api-contract';
import { ApiError, isApiErrorBody } from './api-error';

/**
 * Cliente HTTP del sistema.
 *
 * Responsabilidades deliberadamente acotadas: adjuntar credenciales, aplicar
 * el contrato de respuesta y normalizar errores. No sabe nada de dominio ni
 * de features; cada feature construye sus llamadas sobre el en su carpeta
 * `api/`, que es la unica que puede hablar con el backend (spec 7).
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Tiempo maximo por peticion. Sin esto, una red degradada cuelga la UI. */
const REQUEST_TIMEOUT_MS = 30_000;

type TokenProvider = () => string | null;
type UnauthorizedHandler = () => void;
/** Renueva la sesion. Devuelve `true` si hay un access token nuevo. */
type SessionRefresher = () => Promise<boolean>;

let getAccessToken: TokenProvider = () => null;
let onUnauthorized: UnauthorizedHandler = () => undefined;
let refreshSession: SessionRefresher = () => Promise.resolve(false);

/**
 * Inyecta el acceso a la sesion.
 *
 * Se hace por registro y no importando el store de auth para no crear una
 * dependencia de shared/ hacia una feature, que romperia la arquitectura.
 */
export function configureApiClient(options: {
  tokenProvider: TokenProvider;
  unauthorizedHandler: UnauthorizedHandler;
  sessionRefresher: SessionRefresher;
}): void {
  getAccessToken = options.tokenProvider;
  onUnauthorized = options.unauthorizedHandler;
  refreshSession = options.sessionRefresher;
}

/**
 * Renovacion en curso, compartida.
 *
 * Cuando caduca el access token, varias consultas fallan a la vez. Sin este
 * candado, cada una lanzaria su propia renovacion: como el backend ROTA el
 * refresh token en cada uso, la segunda llegaria con un token ya rotado y el
 * servidor lo interpretaria -con razon- como reutilizacion, revocando la
 * familia entera y echando al usuario. El candado convierte esa avalancha en
 * una sola renovacion que todas esperan.
 */
let renovacionEnCurso: Promise<boolean> | null = null;

function renovarUnaSolaVez(): Promise<boolean> {
  renovacionEnCurso ??= refreshSession().finally(() => {
    renovacionEnCurso = null;
  });

  return renovacionEnCurso;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | undefined>;
  readonly signal?: AbortSignal;
  /** Clave de idempotencia para operaciones criticas (spec 36). */
  readonly idempotencyKey?: string;
}

/** Ejecuta una peticion y devuelve `data` ya desempaquetado del sobre. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestWithRenewal(path, options);
  const payload = await readJson(response, path);

  if (!response.ok) throw toApiError(payload, response.status, path);

  // 204 y respuestas sin cuerpo son validas: devuelven `undefined` tipado.
  if (payload === null) return undefined as T;

  return (payload as ApiSuccess<T>).data;
}

/** Variante que devuelve tambien `meta`, necesaria en listados paginados. */
export async function apiRequestWithMeta<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: Readonly<Record<string, unknown>> }> {
  const response = await requestWithRenewal(path, options);
  const payload = await readJson(response, path);

  if (!response.ok) throw toApiError(payload, response.status, path);
  if (payload === null) throw ApiError.fromMalformedResponse(response.status, path);

  const envelope = payload as ApiSuccess<T>;
  return { data: envelope.data, meta: envelope.meta ?? {} };
}

export interface DownloadedFile {
  readonly blob: Blob;
  /** Cabecera `Content-Disposition` cruda. Quien llama decide el nombre. */
  readonly contentDisposition: string | null;
}

/**
 * Descarga un fichero generado por la API (por ahora, los CSV de reportes).
 *
 * Vive aqui y no en la feature porque necesita exactamente lo que este modulo
 * ya resuelve y nadie mas deberia repetir: el token en la cabecera, la
 * renovacion de sesion compartida, el tiempo maximo de espera y el mismo
 * `ApiError` que el resto de la aplicacion sabe leer. Un `fetch` suelto en una
 * pantalla se saltaria las cuatro cosas.
 *
 * El cuerpo del error SI es JSON aunque el exito no lo sea: un 403 por falta
 * de `report.export` llega con el sobre de siempre, y por eso se lee con el
 * mismo camino que el resto.
 */
export async function apiDownload(
  path: string,
  options: RequestOptions = {},
): Promise<DownloadedFile> {
  const response = await requestWithRenewal(path, options);

  if (!response.ok) throw toApiError(await readJson(response, path), response.status, path);

  return {
    blob: await response.blob(),
    contentDisposition: response.headers.get('content-disposition'),
  };
}

/**
 * Ejecuta la peticion y, si el access token acaba de caducar, la reintenta una
 * sola vez con uno nuevo.
 *
 * Sin esto, el usuario perderia lo que estuviera haciendo cada quince minutos.
 * El reintento es UNICO y solo tras una renovacion con exito: reintentar en
 * bucle ante un 401 persistente convertiria un problema de credenciales en una
 * tormenta de peticiones.
 */
async function requestWithRenewal(path: string, options: RequestOptions): Promise<Response> {
  const response = await performRequest(path, options);

  // Las propias rutas de sesion quedan fuera: renovar la renovacion no tiene
  // sentido y produciria recursion.
  if (response.status !== 401 || path.startsWith('/auth/')) return response;

  const renovado = await renovarUnaSolaVez();
  if (!renovado) return response;

  return performRequest(path, options);
}

async function performRequest(path: string, options: RequestOptions): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  // Si quien llama ya trae su propio signal (React Query lo hace al cancelar
  // una consulta), se encadena para que cualquiera de los dos aborte.
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers: buildHeaders(options),
      body: options.body === undefined ? null : JSON.stringify(options.body),
      signal: controller.signal,
      credentials: 'include',
    });
  } catch (cause) {
    throw ApiError.fromNetworkFailure(cause);
  } finally {
    clearTimeout(timeout);
  }
}

function buildHeaders(options: RequestOptions): HeadersInit {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const token = getAccessToken();
  if (token !== null) headers.Authorization = `Bearer ${token}`;

  if (options.idempotencyKey !== undefined) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }

  return headers;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (query === undefined) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }

  const serialized = params.toString();
  return serialized.length > 0 ? `${url}?${serialized}` : url;
}

async function readJson(response: Response, path: string): Promise<unknown> {
  if (response.status === 204) return null;

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    // Un cuerpo no-JSON en una API JSON suele significar que un proxy o un
    // portal cautivo interceptaron la peticion.
    if (response.ok) throw ApiError.fromMalformedResponse(response.status, path);
    return null;
  }

  try {
    return (await response.json()) as unknown;
  } catch {
    throw ApiError.fromMalformedResponse(response.status, path);
  }
}

function toApiError(payload: unknown, status: number, path: string): ApiError {
  if (!isApiErrorBody(payload)) return ApiError.fromMalformedResponse(status, path);

  const error = new ApiError(payload);

  // La sesion caduca en un unico lugar: si cada feature lo gestionara por su
  // cuenta, bastaria con olvidarlo en una para dejar al usuario atrapado.
  if (error.isUnauthenticated) onUnauthorized();

  return error;
}
